// Copyright (C) 2017-2023 Smart code 203358507

const React = require('react');
const { useNavigate } = require('react-router');
const { default: toPath } = require('stremio-router/toPath');
const { useGoBack } = require('stremio-router');
const PropTypes = require('prop-types');
const classnames = require('classnames');
const { useTranslation } = require('react-i18next');
const { default: Icon } = require('@stremio/stremio-icons/react');
const { Button, Image } = require('stremio/components');
const { useCore } = require('stremio/core');
const Stream = require('./Stream');
const styles = require('./styles');
const { useProfile } = require('stremio/common');
const { default: SeasonEpisodePicker } = require('../EpisodePicker');
const { streamVerificationGroup } = require('../../../caroline/playback/verification');
const { registerManualSources, manualSourcesGroup } = require('../../../caroline/playback/manualSources');
const isSubscriptionStream = require('../../../caroline/playback/isSubscriptionStream');
const streamQuality = require('./streamQuality');
const streamLanguage = require('./streamLanguage');
const resourceFailed = require('../../../caroline/addons/resourceFailure');
const useSlowResource = require('../../../caroline/addons/useSlowResource');

const extractAudioLanguage = (text) => {
    if (!text || typeof text !== 'string') return null;
    const lower = text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    if (lower.includes('portugues e ingles') || lower.includes('ingles e portugues') || lower.includes('dual audio') || lower.includes('dual-audio') || lower.includes('dublado e legendado')) {
        return 'Português e Inglês';
    }
    if (lower.includes('pt-br') || lower.includes('pt_br')) {
        return 'Português (Brasil)';
    }
    if (lower.includes('pt-pt') || lower.includes('pt_pt')) {
        return 'Português (Portugal)';
    }
    if (lower.includes('portugues')) {
        return 'Português';
    }
    if (lower.includes('dublado') || /\bdub\b/.test(lower)) {
        return 'Dublado';
    }
    if (lower.includes('legendado') || /\bleg\b/.test(lower) || lower.includes('subbed')) {
        return 'Legendado';
    }
    if (lower.includes('nacional')) {
        return 'Nacional';
    }
    if (lower.includes('ingles') || lower.includes('english')) {
        return 'Inglês';
    }
    return null;
};

const formatStreamLabels = (stream, index) => {
    const rawName = typeof stream.name === 'string' ? stream.name.trim() : '';
    const rawTitle = typeof stream.title === 'string' ? stream.title.trim() : '';
    const rawDesc = typeof stream.description === 'string' ? stream.description.trim() : '';
    const fullText = `${rawName}\n${rawTitle}\n${rawDesc}`;

    const quality = streamQuality(stream);
    const audioLanguage = streamLanguage(stream) || extractAudioLanguage(fullText);

    // Completely mask third-party addon / source names (FrostStream, Zeus, FenixFlix, Torrentio, etc.)
    const serverTitle = `Servidor ${index + 1}`;
    let qualityBadge = quality || '';
    if (!qualityBadge) {
        if (/\b(?:4k|2160p|uhd)\b/i.test(fullText)) {
            qualityBadge = '4K';
        } else if (/\b(?:1080p|fhd|full[\s-]?hd)\b/i.test(fullText)) {
            qualityBadge = '1080p';
        } else if (/\b(?:720p|hd)\b/i.test(fullText)) {
            qualityBadge = '720p';
        } else if (/\b(?:480p|sd)\b/i.test(fullText)) {
            qualityBadge = '480p';
        }
    }

    const displayName = qualityBadge ? `${serverTitle}\n${qualityBadge}` : serverTitle;

    // Build masked, clean description
    const descriptionParts = [];

    // 1. Language / Audio (e.g. "Português e Inglês", "Português", "Dublado", "Legendado")
    if (audioLanguage) {
        descriptionParts.push(audioLanguage);
    }

    // 2. File size (e.g. "1.8 GB", "872 MB")
    const sizeMatch = fullText.match(/\b(\d+(?:\.\d+)?\s*(?:GB|MB|GiB|MiB))\b/i);
    if (sizeMatch) {
        descriptionParts.push(sizeMatch[1].toUpperCase());
    }

    // 3. Audio / Video format specs (HDR, Atmos, 5.1, HEVC)
    if (/\b(?:HDR10\+|HDR10|HDR|Dolby[\s-]?Vision|DV)\b/i.test(fullText)) {
        const hdr = fullText.match(/\b(HDR10\+|HDR10|HDR|Dolby[\s-]?Vision|DV)\b/i)[0];
        descriptionParts.push(hdr.toUpperCase());
    }
    if (/\b(?:Atmos|5\.1|7\.1)\b/i.test(fullText)) {
        const audio = fullText.match(/\b(Atmos|5\.1|7\.1)\b/i)[0];
        descriptionParts.push(audio);
    }
    if (/\b(?:HEVC|x265|H\.?265)\b/i.test(fullText)) {
        descriptionParts.push('HEVC');
    }

    // 5. Fallback if empty
    if (descriptionParts.length === 0) {
        descriptionParts.push('Disponível');
    }

    return {
        name: displayName,
        description: descriptionParts.join(' · '),
        quality: qualityBadge,
    };
};

const StreamsList = ({ className, video, streamId, metaId, mediaName, mediaYear, type, externalPlayerCallbackCanMarkWatched, onEpisodeSearch, onRetry, ...props }) => {
    const { t } = useTranslation();
    const core = useCore();
    const profile = useProfile();
    const navigate = useNavigate();
    const goBack = useGoBack();
    const backButtonOnClick = React.useCallback(() => {
        if (video.deepLinks && typeof video.deepLinks.metaDetailsVideos === 'string') {
            const navigateTo = `${video.deepLinks.metaDetailsVideos}${
                typeof video.season === 'number'
                    ? `?${new URLSearchParams({ 'season': video.season })}`
                    : ''}`;
            navigate(toPath(navigateTo), { replace: true });
        } else {
            goBack();
        }
    }, [video, navigate, goBack]);
    const countLoadingSources = React.useMemo(() => {
        return props.streams.filter((stream) => stream.content.type === 'Loading').length;
    }, [props.streams]);
    const availableStreams = React.useMemo(() => {
        const streamsBySource = props.streams
            .filter((streams) => streams.content.type === 'Ready')
            .reduce((groups, streams) => {
                groups[streams.addon.transportUrl] = streams.content.content.map((stream) => ({
                    ...stream,
                    onClick: () => {
                        core.transport.analytics({
                            event: 'StreamClicked',
                            args: {
                                stream
                            }
                        });
                        if (profile.settings.playerType !== null) {
                            core.transport.dispatch({
                                action: 'MetaDetails',
                                args: {
                                    action: 'ExternalPlayerStreamOpened',
                                    args: stream
                                }
                            });
                        }
                    }
                }));
                return groups;
            }, {});
        const allStreams = Object.values(streamsBySource).flat();
        return allStreams.filter((stream) => !isSubscriptionStream(stream));
    }, [props.streams, profile.settings.playerType, core.transport]);

    const optionsGroup = streamVerificationGroup(type, streamId, video?.id);
    const manualGroup = manualSourcesGroup(type, metaId, video?.id || streamId);
    // Show Core results as they arrive. A separate HTTP/native probe must
    // neither hide a provider's source nor decide whether Stremio can play it.
    const visibleStreams = React.useMemo(() => availableStreams.map((stream) => ({ stream, state: 'unknown' })), [availableStreams]);
    const sourcesSlow = useSlowResource(countLoadingSources > 0 && availableStreams.length === 0, optionsGroup);
    const checking = !sourcesSlow && countLoadingSources > 0 && availableStreams.length === 0;

    React.useEffect(() => {
        if (!checking && manualGroup) registerManualSources(manualGroup, visibleStreams);
    }, [checking, visibleStreams, manualGroup]);

    const handleEpisodePicker = React.useCallback((season, episode) => {
        onEpisodeSearch(season, episode);
    }, [onEpisodeSearch]);

    return (
        <div className={classnames(className, styles['streams-list-container'])}>
            <div className={styles['select-choices-wrapper']}>
                {
                    video ?
                        <React.Fragment>
                            <Button className={classnames(styles['button-container'], styles['back-button-container'])} tabIndex={0} onClick={backButtonOnClick}>
                                <Icon className={styles['icon']} name={'chevron-back'} />
                            </Button>
                            <div className={styles['episode-title']}>
                                {typeof video.season === 'number' && typeof video.episode === 'number'
                                    ? `S${video.season}E${video.episode}${video.title ? ` ${video.title}` : ''}`
                                    : (video.title ?? '')}
                            </div>
                        </React.Fragment>
                        :
                        null
                }
            </div>
            {
                checking ?
                    <div className={styles['probe-loading']} role={'status'}>
                        <Image src={require('/assets/images/caroline-owl.png')} alt={''} />
                        <span>{process.env.NODE_ENV === 'production'
                            ? t('CAROLINE_STREAM_SEARCHING', { defaultValue: 'A procurar opções…' })
                            : t('CAROLINE_STREAM_VERIFYING', { defaultValue: 'A verificar opções disponíveis…' })}</span>
                    </div>
                    :
                    props.streams.length === 0 || props.streams.every((streams) => streams.content.type === 'Err') || (countLoadingSources === 0 && visibleStreams.length === 0) ?
                        <div className={styles['message-container']}>
                            {
                                type === 'series' ?
                                    <SeasonEpisodePicker className={styles['search']} onSubmit={handleEpisodePicker} />
                                    : null
                            }
                            {
                                video?.upcoming ?
                                    <div className={styles['label']}>{t('UPCOMING')}...</div>
                                    : null
                            }
                            <Image className={styles['image']} src={require('/assets/images/caroline-owl.png')} alt={''} />
                            <div className={styles['label']}>{props.streams.some(resourceFailed)
                                ? t('CAROLINE_STREAMS_UNAVAILABLE', { defaultValue: 'Não foi possível consultar algumas fontes. Volta a tentar mais tarde.' })
                                : t('NO_STREAM')}</div>
                            {typeof onRetry === 'function' ? <Button className={styles['retry-button']} onClick={onRetry}>
                                {t('CAROLINE_RETRY', { defaultValue: 'Tentar novamente' })}
                            </Button> : null}
                        </div>
                        :
                        availableStreams.length === 0 ?
                            sourcesSlow ?
                                <div className={styles['message-container']} role={'status'}>
                                    <div className={styles['label']}>{t('CAROLINE_STREAMS_SLOW', { defaultValue: 'As fontes estão a demorar mais do que o esperado. As opções aparecerão quando responderem.' })}</div>
                                </div>
                                :
                                <div className={styles['streams-container']}>
                                    <Stream.Placeholder />
                                    <Stream.Placeholder />
                                </div>
                            :
                            <React.Fragment>
                                <div className={styles['streams-container']}>
                                    {visibleStreams.slice().sort((a, b) => (a.state === 'active' ? 0 : a.state === 'failed' ? 2 : 1) - (b.state === 'active' ? 0 : b.state === 'failed' ? 2 : 1)).map(({ stream }, index) => {
                                        const { name: streamName, description: streamDesc, quality: streamQual } = formatStreamLabels(stream, index);
                                        return (
                                            <Stream
                                                key={index}
                                                videoId={video?.id}
                                                videoReleased={video?.released}
                                                mediaName={mediaName}
                                                mediaYear={mediaYear}
                                                quality={streamQual}
                                                video={video}
                                                name={streamName}
                                                description={streamDesc}
                                                progress={stream.progress}
                                                deepLinks={stream.deepLinks}
                                                externalPlayerCallbackCanMarkWatched={externalPlayerCallbackCanMarkWatched}
                                                onClick={stream.onClick}
                                            />
                                        );
                                    })}
                                </div>
                                {
                                    countLoadingSources > 0 ?
                                        <div className={styles['addons-loading-container']}>
                                            <div className={styles['addons-loading']}>
                                                {t('CAROLINE_STREAM_SEARCHING', { defaultValue: 'A procurar mais opções…' })}
                                            </div>
                                            <span className={styles['addons-loading-bar']}></span>
                                        </div>
                                        :
                                        null
                                }
                            </React.Fragment>
            }
        </div>
    );
};

StreamsList.propTypes = {
    className: PropTypes.string,
    streams: PropTypes.arrayOf(PropTypes.object).isRequired,
    video: PropTypes.object,
    streamId: PropTypes.string,
    metaId: PropTypes.string,
    mediaName: PropTypes.string,
    mediaYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string,
    externalPlayerCallbackCanMarkWatched: PropTypes.bool,
    onEpisodeSearch: PropTypes.func,
    onRetry: PropTypes.func
};

module.exports = StreamsList;
